import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { apiService } from "@/services/apiService";
import { useAuthStore } from "@/stores/auth";
import { PERMISSIONS } from "@/constants/permissions";

export function useCategory() {
  const { t } = useI18n();
  const { hasScope } = useAuthStore();

  const categories = ref([]);
  const error = ref(null);
  const loading = ref(false);

  const canReadCategory = ref(hasScope(PERMISSIONS.CATEGORY_READ));

  const fetchCategoryById = async (id) => {
    if (!canReadCategory.value) {
      error.value = t("crudMessages.readDenied", {
        resource: "categories",
      });
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      return await apiService.get(`/categories/${id}`);
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.fetchError", { resource: "category" });
      return null;
    } finally {
      loading.value = false;
    }
  };

  const fetchCategories = async (skip = 0, limit = 200) => {
    if (!canReadCategory.value) {
      error.value = t("crudMessages.readDenied", {
        resource: "categories",
      });
      return [];
    }
    loading.value = true;
    error.value = null;
    try {
      const response = await apiService.get("/categories/", { skip, limit });
      categories.value = Array.isArray(response?.items) ? response.items : [];
      return categories.value;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.fetchError", { resource: "categories" });
      categories.value = [];
      return [];
    } finally {
      loading.value = false;
    }
  };

  return {
    canReadCategory,
    categories,
    error,
    fetchCategoryById,
    fetchCategories,
    loading,
  };
}
